/**
 * Login Callback Page
 * 
 * This page handles the OIDC redirect callback from Access Identity.
 * It displays a loading state while the authentication is being processed.
 */

import React, { useEffect } from 'react';
import { LoginCallback as IdentityLoginCallback } from '@workspace/identity-react';
import { useAppIdentity } from '../hooks/useAppIdentity';
import { debugOIDCCallback } from '../utils/debugOIDC';
import { startStorageMonitor, stopStorageMonitor } from '../utils/storageMonitor';
import { saveOIDCState } from '../utils/oidcStateManager';

/**
 * LoginCallback component that wraps the @workspace/identity-react LoginCallback
 * and provides a loading UI while authentication is being processed.
 */
export const LoginCallback: React.FC = () => {
  const identity = useAppIdentity();
  
  // Debug OIDC callback on mount
  useEffect(() => {
    debugOIDCCallback();
    
    // Start storage monitoring to track what the identity library is doing
    startStorageMonitor();
    
    // TODO: Remove detailed storage logging before production
    
    // Cleanup storage monitor on unmount
    return () => {
      stopStorageMonitor();
    };
  }, []);
  
  // Monitor identity state changes and handle redirect
  useEffect(() => {
    const handleAuthSuccess = async () => {
      // If authentication succeeds and is no longer loading, redirect to main app
      if (identity.isAuthenticated && !identity.isLoading && identity.user) {
        // Save the authentication state before redirecting
        await saveOIDCState(identity);
        
        // Longer delay to ensure OIDC state is fully persisted to localStorage
        setTimeout(() => {
          const returnUrl = sessionStorage.getItem('auth_return_url') || '/';
          sessionStorage.removeItem('auth_return_url');
          
          // Mark that we have successfully authenticated
          sessionStorage.setItem('oidc_auth_success', Date.now().toString());
          
          window.location.href = returnUrl;
        }, 500); // Increased delay to 500ms
      }
      
      // If there's an error, log it
      if (identity.isError && identity.error) {
        console.error('Authentication error:', identity.error);
      }
    };
    
    handleAuthSuccess().catch(error => {
      console.error('Error in auth success handler:', error);
    });
  }, [identity, identity.isAuthenticated, identity.isLoading, identity.isError, identity.error, identity.user]);
  
  // The LoginCallback from @workspace/identity-react handles the OIDC callback
  // and returns null while processing. We wrap it with a loading UI.
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        backgroundColor: '#f8f9fa',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          maxWidth: '400px',
        }}
      >
        <h2
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '1rem',
          }}
        >
          Completing sign in...
        </h2>
        
        {/* Show different messages based on identity state */}
        {identity.isError ? (
          <div style={{ color: '#E11D48', marginBottom: '1rem' }}>
            <p>Authentication error: {identity.error?.message || 'Unknown error'}</p>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                backgroundColor: '#E11D48',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer',
                marginTop: '8px'
              }}
            >
              Return to App
            </button>
          </div>
        ) : identity.isAuthenticated ? (
          <div style={{ color: '#28a745', marginBottom: '1rem' }}>
            <p>Authentication successful! Redirecting...</p>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '1.5rem',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #E11D48',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
          </div>
        )}
        
        <p
          style={{
            color: '#666',
            fontSize: '0.9rem',
          }}
        >
          Please wait while we complete your authentication with Access Identity.
        </p>
        
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
      
      {/* The actual callback handler from @workspace/identity-react */}
      <IdentityLoginCallback />
    </div>
  );
};

export default LoginCallback;
